// Prediction form for /matches/:id. Three UI states keyed off the match:
//
//   1. Kickoff in the future → editable cascading form (FT → ET → pens).
//   2. Kickoff has passed, match not finished → show the user's locked
//      prediction read-only, or "no prediction placed".
//   3. Match finished → show prediction vs actual with the points
//      breakdown from the server.
//
// The cascading rules mirror PredictionInputSchema in shared/ so the UI
// can never submit something the server would reject.

import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  MatchPublic,
  PredictionInput,
  PredictionPublic,
  ScoringBreakdown,
} from '@mondy/shared';
import {
  canPredictEarly,
  isKnockoutStage,
  isMatchLive,
  predictionWindowState,
  scoreMatch,
} from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { cn } from '@/lib/cn';
import { ScoreInput, type Score } from '@/components/ScoreInput';
import { Spinner } from '@/components/Spinner';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { matchLabel } from '@/lib/format';

interface Props {
  match: MatchPublic;
}

interface PredictionResponse {
  prediction: PredictionPublic | null;
}

function scoreEq(a: Score | null | undefined, b: Score | null | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.team1 === b.team1 && a.team2 === b.team2;
}

// Formats the delta between a target timestamp and "now" as a human
// countdown. Used both for "Locks in …" (target = kickoff) and
// "Opens in …" (target = kickoff − 24h) — the math is identical.
function formatCountdown(targetMs: number): string {
  const delta = targetMs - Date.now();
  if (delta <= 0) return 'now';
  const h = Math.floor(delta / 3_600_000);
  const m = Math.floor((delta % 3_600_000) / 60_000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h === 0) return `${m}m`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export function PredictionForm({ match }: Props) {
  const user = useCurrentUser();

  const isKnockout = match.stage !== 'group';
  const teamsResolved = !!match.team1 && !!match.team2;

  if (user === undefined) {
    return (
      <div className="predict-card" style={{ alignItems: 'center' }}>
        <Spinner />
      </div>
    );
  }
  if (user === null) {
    return (
      <div className="predict-card">
        <p style={mutedText}>
          <Link
            to="/login"
            style={{ color: 'var(--hot)', textDecoration: 'underline' }}
          >
            Log in
          </Link>{' '}
          to place a prediction for this match.
        </p>
      </div>
    );
  }

  if (!teamsResolved) {
    return (
      <div className="predict-card">
        <p style={mutedText}>
          Predictions open once the bracket resolves these teams.
        </p>
      </div>
    );
  }

  const allowEarlyPrediction = canPredictEarly(user.paymentTier);

  return (
    <PredictionFormInner
      match={match}
      isKnockout={isKnockout}
      allowEarlyPrediction={allowEarlyPrediction}
    />
  );
}

function PredictionFormInner({
  match,
  isKnockout,
  allowEarlyPrediction,
}: {
  match: MatchPublic;
  isKnockout: boolean;
  allowEarlyPrediction: boolean;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['prediction', match.id],
    queryFn: () => api.get<PredictionResponse>(`/api/predictions/match/${match.id}`),
  });

  if (query.isLoading) {
    return (
      <div className="predict-card" style={{ alignItems: 'center' }}>
        <Spinner label="Loading your prediction" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="predict-card">
        <p style={{ ...mutedText, color: 'var(--danger)' }}>
          Couldn't load your prediction.
        </p>
      </div>
    );
  }

  const existing = query.data!.prediction;

  // Non-scheduled matches (live/paused/finished) always show the locked
  // read-only view — the 24h window only matters for scheduled matches.
  if (match.status !== 'scheduled') {
    return <LockedState match={match} prediction={existing} />;
  }

  const windowState = predictionWindowState(match, { allowEarlyPrediction });
  if (windowState.state === 'locked') {
    return <LockedState match={match} prediction={existing} />;
  }
  if (windowState.state === 'too_early') {
    return <TooEarlyState opensAtMs={windowState.opensAtMs} />;
  }

  return (
    <Editable
      match={match}
      existing={existing}
      isKnockout={isKnockout}
      onSaved={() => {
        void queryClient.invalidateQueries({ queryKey: ['prediction', match.id] });
        void queryClient.invalidateQueries({ queryKey: ['predictions', 'mine'] });
        void queryClient.invalidateQueries({
          queryKey: ['predictions', 'all', match.id],
        });
      }}
    />
  );
}

function Editable({
  match,
  existing,
  isKnockout,
  onSaved,
}: {
  match: MatchPublic;
  existing: PredictionPublic | null;
  isKnockout: boolean;
  onSaved: () => void;
}) {
  const [ft, setFt] = useState<Score>(existing?.prediction.fullTime ?? { team1: 0, team2: 0 });
  const [et, setEt] = useState<Score>(() => {
    const savedFt = existing?.prediction.fullTime ?? { team1: 0, team2: 0 };
    const savedEt = existing?.prediction.extraTime ?? { team1: 0, team2: 0 };
    return {
      team1: Math.max(savedEt.team1, savedFt.team1),
      team2: Math.max(savedEt.team2, savedFt.team2),
    };
  });
  const [pens, setPens] = useState<Score>(
    existing?.prediction.penalties ?? { team1: 1, team2: 0 },
  );
  const [error, setError] = useState<string | null>(null);

  const ftDraw = ft.team1 === ft.team2;
  const etDraw = et.team1 === et.team2;
  const showEt = isKnockout && ftDraw;
  const showPens = isKnockout && ftDraw && etDraw;

  const effectiveEt = showEt ? et : null;
  const effectivePens = showPens ? pens : null;
  const unchanged =
    !!existing &&
    existing.prediction.fullTime.team1 === ft.team1 &&
    existing.prediction.fullTime.team2 === ft.team2 &&
    scoreEq(existing.prediction.extraTime, effectiveEt) &&
    scoreEq(existing.prediction.penalties, effectivePens);

  const save = useMutation({
    mutationFn: async () => {
      const body: PredictionInput = {
        fullTime: ft,
        extraTime: effectiveEt,
        penalties: effectivePens,
      };
      return api.put<{ prediction: PredictionPublic }>(
        `/api/predictions/${match.id}`,
        body,
      );
    },
    onSuccess: () => {
      setError(null);
      onSaved();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (showPens && pens.team1 === pens.team2) {
      setError('Penalties must have a winner — no ties.');
      return;
    }
    save.mutate();
  }

  const { t1, t2 } = matchLabel(match);
  const kickoffMs = new Date(match.kickoffUtc).getTime();

  return (
    <form onSubmit={onSubmit} className="predict-card">
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h3>Your prediction</h3>
        {existing ? (
          <span
            style={{
              fontSize: 10,
              letterSpacing: '0.22em',
              color: 'var(--fg-muted)',
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            Saved · {existing.prediction.fullTime.team1}–
            {existing.prediction.fullTime.team2}
          </span>
        ) : null}
      </div>

      <ScoreInput
        label="Full time"
        value={ft}
        onChange={(next) => {
          setFt(next);
          setEt((prev) => ({
            team1: Math.max(prev.team1, next.team1),
            team2: Math.max(prev.team2, next.team2),
          }));
        }}
        team1={{
          label: t1,
          flagCode: match.team1?.fifaCode ?? null,
          flagUrl: match.team1?.flagUrl ?? null,
        }}
        team2={{
          label: t2,
          flagCode: match.team2?.fifaCode ?? null,
          flagUrl: match.team2?.flagUrl ?? null,
        }}
      />

      {showEt ? (
        <div style={dashedBox}>
          <ScoreInput
            label="Extra time"
            value={et}
            onChange={(next) =>
              setEt({
                team1: Math.max(next.team1, ft.team1),
                team2: Math.max(next.team2, ft.team2),
              })
            }
          />
        </div>
      ) : null}

      {showPens ? (
        <div style={dashedBox}>
          <ScoreInput label="Penalties" value={pens} onChange={setPens} />
        </div>
      ) : null}

      <div className="predict-lock">
        <span>
          Exact: <b style={{ color: 'var(--fg)' }}>3 pts</b> · Winner:{' '}
          <b style={{ color: 'var(--fg)' }}>1 pt</b> · Wrong:{' '}
          <b style={{ color: 'var(--fg)' }}>0</b>
        </span>
        <span className="countdown">⏱ Locks in {formatCountdown(kickoffMs)}</span>
      </div>

      {error ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>{error}</p>
      ) : null}

      <button
        type="submit"
        disabled={save.isPending || unchanged}
        className="btn btn-primary btn-full"
      >
        {save.isPending
          ? 'Saving…'
          : unchanged
            ? `Saved · ${ft.team1}–${ft.team2}`
            : existing
              ? `Update · ${ft.team1}–${ft.team2}`
              : `Place prediction · ${ft.team1}–${ft.team2}`}
      </button>
    </form>
  );
}

function LockedState({
  match,
  prediction,
}: {
  match: MatchPublic;
  prediction: PredictionPublic | null;
}) {
  const finished = match.status === 'finished' && match.result !== null;

  if (!prediction) {
    return (
      <div className="predict-card">
        <p style={mutedText}>
          {finished
            ? 'You didn’t place a prediction on this match.'
            : 'Predictions are locked — kick-off already started.'}
        </p>
      </div>
    );
  }

  // While live/paused and not yet scored, compute per-stage provisional
  // points client-side with the canonical scorer. Finished matches keep
  // using the server's pointsBreakdown untouched.
  const live = isMatchLive(match);
  const provisional =
    live && prediction.pointsAwarded == null && match.result?.fullTime
      ? scoreMatch(prediction.prediction, match.result, isKnockoutStage(match.stage))
      : null;

  return (
    <div className="predict-card">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h3>Your prediction</h3>
        {finished && typeof prediction.pointsAwarded === 'number' ? (
          <span
            className="pts tabular"
            style={{
              fontSize: 13,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              padding: '4px 12px',
              borderRadius: 999,
              background:
                prediction.pointsAwarded >= 3 ? 'var(--turf-soft)' : 'var(--sun-soft)',
              color:
                prediction.pointsAwarded >= 3 ? 'var(--turf)' : 'var(--sun)',
              letterSpacing: '0.08em',
            }}
          >
            +{prediction.pointsAwarded} PTS
          </span>
        ) : (
          <span
            style={{
              fontSize: 10,
              letterSpacing: '0.22em',
              color: 'var(--fg-muted)',
              textTransform: 'uppercase',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 999,
              background: 'var(--bg)',
              border: '1px solid var(--line)',
            }}
          >
            Locked
          </span>
        )}
      </div>

      <StageRow
        label="Full time"
        predicted={prediction.prediction.fullTime}
        actual={
          (finished || provisional) && match.result ? match.result.fullTime : null
        }
        points={
          finished
            ? prediction.pointsBreakdown?.fullTime
            : provisional?.breakdown.fullTime
        }
        provisional={!!provisional}
      />

      {prediction.prediction.extraTime ? (
        <StageRow
          label="Extra time"
          predicted={prediction.prediction.extraTime}
          actual={
            (finished || provisional) && match.result ? match.result.extraTime : null
          }
          points={
            finished
              ? prediction.pointsBreakdown?.extraTime
              : provisional && match.result?.extraTime
                ? provisional.breakdown.extraTime
                : undefined
          }
          provisional={!!provisional}
        />
      ) : null}

      {prediction.prediction.penalties ? (
        <StageRow
          label="Penalties"
          predicted={prediction.prediction.penalties}
          actual={
            (finished || provisional) && match.result ? match.result.penalties : null
          }
          points={
            finished
              ? prediction.pointsBreakdown?.penalties
              : provisional && match.result?.penalties
                ? provisional.breakdown.penalties
                : undefined
          }
          provisional={!!provisional}
        />
      ) : null}

      {finished && !prediction.pointsBreakdown ? (
        <p style={tinyMuted}>Points are being calculated…</p>
      ) : null}
    </div>
  );
}

function TooEarlyState({ opensAtMs }: { opensAtMs: number }) {
  return (
    <div className="predict-card">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <h3>Your prediction</h3>
        <span
          style={{
            fontSize: 10,
            letterSpacing: '0.22em',
            color: 'var(--fg-muted)',
            textTransform: 'uppercase',
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: 999,
            background: 'var(--bg)',
            border: '1px solid var(--line)',
          }}
        >
          Opens soon
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 4,
          padding: '18px 20px',
          background: 'var(--bg)',
          border: '1px solid var(--line)',
          borderRadius: 14,
        }}
      >
        <span
          style={{
            fontSize: 10,
            letterSpacing: '0.22em',
            color: 'var(--fg-muted)',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          Opens in
        </span>
        <span
          className="tabular"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 32,
            letterSpacing: '0.04em',
            color: 'var(--fg)',
            lineHeight: 1.1,
          }}
        >
          {formatCountdown(opensAtMs)}
        </span>
      </div>

      <p style={{ ...mutedText, margin: 0 }}>
        Predictions open 24 hours before kick-off. Come back then to place your
        pick.
      </p>
    </div>
  );
}

function StageRow({
  label,
  predicted,
  actual,
  points,
  provisional = false,
}: {
  label: string;
  predicted: Score;
  actual: Score | null;
  points: ScoringBreakdown[keyof ScoringBreakdown] | undefined;
  provisional?: boolean;
}) {
  const ptsClass =
    typeof points === 'number'
      ? points >= 3
        ? 'w3'
        : points >= 1
          ? 'w1'
          : 'w0'
      : '';
  // Live provisional pill — dashed outline + tinted text, matching the
  // PointsBadge scheme in MatchPredictions so "pending" reads consistently.
  const provisionalStyle =
    provisional && typeof points === 'number'
      ? points >= 3
        ? {
            border: '1px dashed color-mix(in oklab, var(--turf) 40%, transparent)',
            color: 'var(--turf)',
            background: 'transparent',
          }
        : points >= 1
          ? {
              border: '1px dashed color-mix(in oklab, var(--sun) 40%, transparent)',
              color: 'var(--sun)',
              background: 'transparent',
            }
          : {
              border: '1px dashed var(--line)',
              color: 'var(--fg-muted)',
              background: 'transparent',
            }
      : undefined;
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        background: 'var(--bg)',
        border: '1px solid var(--line)',
        borderRadius: 12,
      }}
    >
      <span
        style={{
          fontSize: 10,
          letterSpacing: '0.22em',
          color: 'var(--fg-muted)',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          fontFamily: 'var(--font-display)',
          fontSize: 22,
          letterSpacing: '0.04em',
        }}
      >
        <span className="tabular">
          {predicted.team1}–{predicted.team2}
        </span>
        {actual ? (
          <>
            <span
              style={{
                fontSize: 10,
                letterSpacing: '0.22em',
                color: 'var(--fg-muted)',
                textTransform: 'uppercase',
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
              }}
            >
              vs
            </span>
            <span className="tabular" style={{ color: 'var(--hot)' }}>
              {actual.team1}–{actual.team2}
            </span>
          </>
        ) : null}
        {typeof points === 'number' ? (
          <span
            className={cn('pts', ptsClass)}
            title={
              provisional ? 'Provisional — updates as the score changes' : undefined
            }
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              padding: '3px 10px',
              borderRadius: 999,
              fontWeight: 700,
              ...provisionalStyle,
            }}
          >
            +{points}
          </span>
        ) : null}
      </div>
    </div>
  );
}

const mutedText = {
  fontSize: 13,
  color: 'var(--fg-muted)',
} as const;

const tinyMuted = {
  fontSize: 11,
  color: 'var(--fg-muted)',
  marginTop: 4,
} as const;

const dashedBox = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 12,
  padding: 16,
  borderRadius: 14,
  border: '1px dashed var(--line)',
  background: 'var(--bg)',
};
